import { fabric } from 'fabric';
import { useCallback } from 'react';
import Vec2 from 'vec2';

import {
  CANVAS_OBJECT_TYPE_PREFIX,
  CANVAS_OBJECT_TYPE_SUFFIX,
} from '~/core/constants';
import {
  getOutlinePointsFromShape,
  isPointInsidePolygon,
} from '~/core/helpers';
import {
  useDrawingShapeContext,
  useFabricCanvasContext,
} from '~/legacy/easy-estimator-page/context';
import { findAppliedApplianceName } from '~/legacy/easy-estimator-page/context/drawingShapeContext/drawingShape.utils';
import { useAppDispatch } from '~/redux/hooks';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { updateSelectedDrawingObjectName } from '~/redux/slices/shape';

export const useMouseDownBefore = (canvas: fabric.Canvas) => {
  const dispatch = useAppDispatch();

  const {
    getters: { drawingAppliances, activeDrawingApplianceId },
    handlers: { updateActiveDrawingAppliance, updateActiveDrawingShapeId },
  } = useDrawingShapeContext();

  const {
    drawingShapesRef,
    isShapeLogicRef,
    movingItemRef,
    shapeStartDragRef,
  } = useFabricCanvasContext();

  const handleSelectionCircle = useCallback(
    (pointer: Vec2) => {
      const shape = drawingShapesRef.current.find((shape) =>
        isPointInsidePolygon(getOutlinePointsFromShape(shape), pointer)
      );

      if (shape) {
        updateActiveDrawingShapeId(shape.id);
        dispatch(updateNeedRedrawCanvas(true));
      }
    },
    [dispatch, drawingShapesRef, updateActiveDrawingShapeId]
  );

  const handleApplianceInteraction = useCallback(
    (
      target: fabric.Object,
      groupMemberClicked: fabric.Object | undefined,
      targetName: string | undefined,
      pointer: Vec2
    ) => {
      isShapeLogicRef.current = true;
      if (!groupMemberClicked?.name) {
        if (target.name) {
          const appliance = drawingAppliances.find(
            (appliance) =>
              appliance.id ===
              target.name?.replace(
                `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_`,
                ''
              )
          );

          if (appliance) {
            const {
              applianceModel: { consumerDesc, name },
            } = appliance;

            dispatch(updateSelectedDrawingObjectName(consumerDesc ?? name));
          }
        }

        return;
      }
      const shape = drawingShapesRef.current.find(
        (dShape) =>
          targetName ===
          dShape.canvasObjectName + CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP
      );

      if (
        shape &&
        groupMemberClicked?.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE)
      ) {
        const applianceId = groupMemberClicked.name;
        const applianceName = findAppliedApplianceName(
          applianceId,
          drawingShapesRef.current
        );

        // Move previously selected appliance back to its group (if exists and different)
        if (
          activeDrawingApplianceId &&
          activeDrawingApplianceId !== applianceId
        ) {
          const previousApplianceObject = canvas
            .getObjects()
            .find(
              (obj) =>
                obj.name === activeDrawingApplianceId && obj.type === 'image'
            ) as fabric.Image | undefined;

          if (previousApplianceObject) {
            // Find which shape contains the previous appliance
            const previousShape = drawingShapesRef.current.find((dShape) =>
              dShape.appliances.some(
                (appliance) =>
                  `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}` ===
                  activeDrawingApplianceId
              )
            );

            if (previousShape && previousShape.fabricGroup) {
              canvas.remove(previousApplianceObject);
              previousShape.fabricGroup.addWithUpdate(previousApplianceObject);
            }
          }
        }

        target.lockMovementX = true;
        target.lockMovementY = true;
        isShapeLogicRef.current = false;
        movingItemRef.current = applianceId;
        shapeStartDragRef.current = pointer;

        // Move appliance from group to canvas directly without full redraw
        shape.fabricGroup.remove(groupMemberClicked);
        canvas.add(groupMemberClicked as fabric.Image);
        canvas.setActiveObject(groupMemberClicked as fabric.Image);

        updateActiveDrawingAppliance(applianceId);
        dispatch(updateSelectedDrawingObjectName(applianceName));
        // Don't trigger full redraw - we've moved the appliance directly
        canvas.renderAll();
      }
    },
    [
      canvas,
      dispatch,
      drawingAppliances,
      drawingShapesRef,
      isShapeLogicRef,
      movingItemRef,
      shapeStartDragRef,
      updateActiveDrawingAppliance,
      activeDrawingApplianceId,
    ]
  );

  return {
    handleSelectionCircle,
    handleApplianceInteraction,
  };
};
